import { defineStore } from "pinia";
import { ref, computed } from "vue";
import permissionApi, {
    type Permission,
    type PermissionFormData,
} from "@/api/permission";

export const usePermissionStore = defineStore("permission", () => {
    // State
    const permissions = ref<Permission[]>([]);
    const currentPermission = ref<Permission | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const group = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const totalPages = computed(() => meta.value?.last_page || 0);
    const paginatedData = computed(() => permissions.value);
    const filteredPermissions = computed(() => {
        let filtered = permissions.value;

        if (search.value) {
            filtered = filtered.filter((permission: Permission) =>
                permission.name
                    .toLowerCase()
                    .includes(search.value.toLowerCase()),
            );
        }

        if (group.value) {
            filtered = filtered.filter(
                (permission: Permission) =>
                    permission.group_name === group.value,
            );
        }

        return filtered;
    });
    const hasPermissions = computed(() => permissions.value.length > 0);
    const isLoading = computed(() => loading.value);

    // Actions
    const fetchPermissions = async (params?: {
        page?: number;
        per_page?: number;
        search?: string;
        group?: string;
    }) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await permissionApi.getPermissions({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                group: group.value || undefined,
                ...params,
            });

            permissions.value = response.data?.items || response.data || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message ||
                    "Failed to fetch permissions";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchPermission = async (id: number): Promise<any> => {
        loading.value = true;
        error.value = null;

        try {
            const response = await permissionApi.getPermission(id);
            currentPermission.value = response.data;
            return currentPermission.value;
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message || "Failed to fetch permission";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createPermission = async (data: PermissionFormData) => {
        loading.value = true;
        error.value = null;

        try {
            const newPermission = await permissionApi.createPermission(data);

            // Add to local list immediately to avoid refetch issues
            if (newPermission.data) {
                permissions.value.unshift(newPermission.data);
            }

            return newPermission;
        } catch (err: any) {
            // Handle validation errors
            if (err.response?.status === 422 && err.response?.data?.errors) {
                const validationErrors = err.response.data.errors;
                const firstError = Object.values(validationErrors)[0];
                error.value = Array.isArray(firstError)
                    ? firstError[0]
                    : firstError;
            } else {
                error.value =
                    err.response?.data?.message ||
                    "Failed to create permission";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updatePermission = async (id: number, data: PermissionFormData) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedPermission = await permissionApi.updatePermission(
                id,
                data,
            );

            // Update permission in local list
            const index = permissions.value.findIndex(
                (permission: Permission) => permission.id === id,
            );
            if (index !== -1) {
                permissions.value[index] = updatedPermission.data;
            }

            // Update current permission if it's the one being edited
            if (currentPermission.value?.id === id) {
                currentPermission.value = updatedPermission.data;
            }

            return updatedPermission;
        } catch (err: any) {
            // Handle validation errors
            if (err.response?.status === 422 && err.response?.data?.errors) {
                const validationErrors = err.response.data.errors;
                const firstError = Object.values(validationErrors)[0];
                error.value = Array.isArray(firstError)
                    ? firstError[0]
                    : firstError;
            } else {
                error.value =
                    err.response?.data?.message ||
                    "Failed to update permission";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deletePermission = async (id: number) => {
        loading.value = true;
        error.value = null;

        try {
            await permissionApi.deletePermission(id);

            // Remove from local list
            permissions.value = permissions.value.filter(
                (permission: Permission) => permission.id !== id,
            );

            // Clear current permission if it's the one being deleted
            if (currentPermission.value?.id === id) {
                currentPermission.value = null;
            }
        } catch (err: any) {
            // Handle validation errors
            if (err.response?.status === 422 && err.response?.data?.errors) {
                const validationErrors = err.response.data.errors;
                const firstError = Object.values(validationErrors)[0];
                error.value = Array.isArray(firstError)
                    ? firstError[0]
                    : firstError;
            } else {
                error.value =
                    err.response?.data?.message ||
                    "Failed to delete permission";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchPermissionGroups = async () => {
        loading.value = true;
        error.value = null;

        try {
            const response = await permissionApi.getPermissionGroups();
            return response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch permission groups";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchPermissionsByGroup = async () => {
        loading.value = true;
        error.value = null;

        try {
            const response = await permissionApi.getPermissionsByGroup();
            return response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch permissions by group";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
        fetchPermissions();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
        fetchPermissions();
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        group.value = "";
        fetchPermissions();
    };

    return {
        // State
        permissions,
        currentPermission,
        loading,
        error,
        page,
        perPage,
        search,
        group,
        meta,

        // Getters
        totalPages,
        paginatedData,
        filteredPermissions,
        hasPermissions,
        isLoading,

        // Actions
        fetchPermissions,
        fetchPermission,
        createPermission,
        updatePermission,
        deletePermission,
        fetchPermissionGroups,
        fetchPermissionsByGroup,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
